import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Upload, Trash2, Check, Clock, Image as ImageIcon } from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useMedia,
  useUploadMedia,
  useDeleteMedia,
  usePatchMedia,
  useMarkConsent,
  transformedUrl,
  type MediaAsset,
  type MediaKind,
} from '@/lib/media';
import { useBrief, useMe } from '@/lib/brief';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/media')({
  staticData: { crumb: 'Media' },
  component: MediaPage,
});

const KIND_LABELS: Record<MediaKind, string> = {
  logo: 'Logo',
  clinic_interior: 'Wnętrze kliniki',
  equipment: 'Sprzęt',
  team_photo: 'Zdjęcie zespołu',
  patient_photo: 'Zdjęcie pacjenta',
  other: 'Inne',
};

function MediaPage() {
  const media = useMedia();
  const upload = useUploadMedia();
  const remove = useDeleteMedia();
  const patch = usePatchMedia();
  const consent = useMarkConsent();
  const { data: brief } = useBrief();
  const me = useMe();
  const team = ((brief?.team ?? []) as Array<{ id: string; full_name: string }>) || [];

  const [uploadKind, setUploadKind] = React.useState<MediaKind>('clinic_interior');
  const [linkedDoctor, setLinkedDoctor] = React.useState<string>('');
  const [altText, setAltText] = React.useState('');
  const [dragOver, setDragOver] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [filterKind, setFilterKind] = React.useState<MediaKind | 'all'>('all');

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const onFiles = async (files: FileList | File[]) => {
    setFeedback(null);
    const list = Array.from(files);
    let ok = 0;
    for (const file of list) {
      try {
        await upload.mutateAsync({
          file,
          kind: uploadKind,
          alt_text: altText || undefined,
          linked_doctor_id: linkedDoctor || undefined,
        });
        ok += 1;
      } catch (e) {
        const msg = (e as Error).message;
        setFeedback({
          kind: 'err',
          text:
            msg === 'tier_item_limit_reached'
              ? 'Limit liczby plików dla Twojego planu osiągnięty.'
              : msg === 'tier_storage_limit_reached'
              ? 'Limit miejsca dla Twojego planu osiągnięty.'
              : msg === 'unsupported_mime'
              ? 'Ten format pliku nie jest obsługiwany.'
              : msg === 'file_too_large'
              ? 'Plik większy niż 15 MB.'
              : 'Nie udało się wczytać pliku.',
        });
        break;
      }
    }
    if (ok > 0) {
      setFeedback({ kind: 'ok', text: `Wczytano ${ok} ${ok === 1 ? 'plik' : 'plików'}.` });
      setAltText('');
    }
  };

  const filtered = (media.data?.assets ?? []).filter(
    (a) => filterKind === 'all' || a.kind === filterKind
  );
  const usage = media.data?.usage;
  const usagePct = usage ? Math.min(100, Math.round((usage.bytes / (usage.limit_mb * 1024 * 1024)) * 100)) : 0;
  const itemsPct = usage ? Math.min(100, Math.round((usage.items / usage.limit_items) * 100)) : 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">Media</h1>
        <p className="mt-1 text-sm text-text-muted">
          Biblioteka zdjęć kliniki, zespołu i sprzętu. Optymalizujemy w locie do
          AVIF/WebP. Zdjęcia osób identyfikowalnych wymagają podpisanej zgody.
        </p>
      </header>

      {usage ? (
        <Card>
          <CardHeader>
            <CardTitle>Wykorzystanie</CardTitle>
            <CardDescription>
              Plan {me.data?.tenant.tier?.toUpperCase()} — limit zależy od subskrypcji.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <UsageBar
              label="Liczba plików"
              value={`${usage.items} / ${usage.limit_items}`}
              pct={itemsPct}
            />
            <UsageBar
              label="Miejsce"
              value={`${usage.mb} MB / ${usage.limit_mb} MB`}
              pct={usagePct}
            />
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Dodaj zdjęcia</CardTitle>
          <CardDescription>
            Przeciągnij pliki na obszar poniżej lub kliknij, aby wybrać. Obsługujemy JPEG,
            PNG, WebP, AVIF i HEIC (do 15 MB).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Typ pliku" htmlFor="upload-kind">
              <Select
                id="upload-kind"
                value={uploadKind}
                onChange={(e) => setUploadKind(e.target.value as MediaKind)}
              >
                {(Object.keys(KIND_LABELS) as MediaKind[]).map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABELS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            {uploadKind === 'team_photo' ? (
              <Field label="Powiązany członek zespołu" htmlFor="linked-doctor">
                <Select
                  id="linked-doctor"
                  value={linkedDoctor}
                  onChange={(e) => setLinkedDoctor(e.target.value)}
                >
                  <option value="">— wybierz —</option>
                  {team.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.full_name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
              <Field label="Opis (alt)" htmlFor="alt-text" hint="dla dostępności + SEO">
                <Input
                  id="alt-text"
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  placeholder="Np. wnętrze gabinetu zabiegowego"
                />
              </Field>
            )}
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files);
            }}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') inputRef.current?.click();
            }}
            className={cn(
              'grid h-44 cursor-pointer place-items-center rounded-[var(--radius-card)] border-2 border-dashed text-center transition-colors',
              dragOver ? 'border-accent bg-accent/5' : 'border-border bg-surface hover:bg-bg'
            )}
          >
            <div>
              <Upload className="mx-auto size-7 text-text-subtle" aria-hidden="true" />
              <p className="mt-2 text-sm font-medium text-text">
                Przeciągnij pliki tutaj lub kliknij, aby wybrać
              </p>
              <p className="text-xs text-text-subtle">
                Wczytamy w tle. Optymalizujemy automatycznie.
              </p>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif,image/heic"
              multiple
              className="sr-only"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) onFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>

          {feedback ? (
            <p
              className={
                feedback.kind === 'ok'
                  ? 'rounded-md bg-success/10 p-3 text-sm text-success ring-1 ring-success/20'
                  : 'rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20'
              }
              role="status"
            >
              {feedback.text}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="size-5" aria-hidden="true" /> Biblioteka
            <span className="ml-2 text-sm font-normal text-text-subtle tabular-nums">
              {filtered.length}
            </span>
          </CardTitle>
          <CardDescription>
            Filtruj po typie. Kliknij plik, aby edytować opis lub powiązanie z zespołem.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-wrap gap-1.5">
            <FilterChip active={filterKind === 'all'} onClick={() => setFilterKind('all')}>
              Wszystkie
            </FilterChip>
            {(Object.keys(KIND_LABELS) as MediaKind[]).map((k) => (
              <FilterChip
                key={k}
                active={filterKind === k}
                onClick={() => setFilterKind(k)}
              >
                {KIND_LABELS[k]}
              </FilterChip>
            ))}
          </div>
          {media.isPending ? (
            <p className="text-sm text-text-muted">Ładowanie…</p>
          ) : filtered.length === 0 ? (
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Brak plików. Dodaj pierwsze zdjęcie z formularza powyżej.
            </p>
          ) : (
            <ul role="list" className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {filtered.map((a) => (
                <MediaCard
                  key={a.id}
                  asset={a}
                  team={team}
                  onDelete={() => remove.mutate(a.id)}
                  onPatch={(input) => patch.mutate({ id: a.id, ...input })}
                  onConsent={(input) => consent.mutate({ id: a.id, ...input })}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UsageBar({ label, value, pct }: { label: string; value: string; pct: number }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <p className="text-xs uppercase tracking-wide text-text-subtle">{label}</p>
        <p className="text-sm font-medium text-text tabular-nums">{value}</p>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface">
        <div
          className={cn(
            'h-full transition-all',
            pct >= 90 ? 'bg-error' : pct >= 70 ? 'bg-warning' : 'bg-accent'
          )}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
        />
      </div>
    </div>
  );
}

function FilterChip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-3 py-1 text-xs font-medium ring-1 transition-colors',
        active
          ? 'bg-accent/10 text-accent ring-accent/20'
          : 'bg-bg text-text-muted ring-border hover:bg-surface'
      )}
    >
      {children}
    </button>
  );
}

function MediaCard({
  asset,
  team,
  onDelete,
  onPatch,
  onConsent,
}: {
  asset: MediaAsset;
  team: Array<{ id: string; full_name: string }>;
  onDelete: () => void;
  onPatch: (i: { alt_text?: string; linked_doctor_id?: string | null }) => void;
  onConsent: (i: { subject_kind?: 'doctor' | 'patient'; subject_id?: string }) => void;
}) {
  const needsConsent = (asset.kind === 'team_photo' || asset.kind === 'patient_photo') && asset.consent_signed_at == null;
  const [editing, setEditing] = React.useState(false);
  const [alt, setAlt] = React.useState(asset.alt_text ?? '');
  const [doc, setDoc] = React.useState(asset.linked_doctor_id ?? '');

  // For dev mode, render the raw R2-backed URL via the api-worker dev proxy.
  // In production this becomes a CF-transformed URL.
  const thumb = env.apiBaseUrl
    ? `${env.apiBaseUrl}${asset.public_url}`
    : transformedUrl(asset.public_url, { width: 400, fit: 'cover' });

  return (
    <li className="rounded-[var(--radius-card)] bg-bg ring-1 ring-black/5 overflow-hidden">
      <div className="relative aspect-square bg-surface">
        <img
          src={thumb}
          alt={asset.alt_text ?? asset.original_filename}
          loading="lazy"
          className="size-full object-cover"
        />
        {needsConsent ? (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-warning/90 px-2 py-0.5 text-[10px] font-medium text-text">
            <Clock className="size-3" aria-hidden="true" /> Zgoda
          </span>
        ) : asset.consent_signed_at ? (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-success/90 px-2 py-0.5 text-[10px] font-medium text-text-inverted">
            <Check className="size-3" aria-hidden="true" /> Zgoda
          </span>
        ) : null}
      </div>
      <div className="p-3">
        <p className="truncate text-xs text-text-subtle font-mono">{asset.original_filename}</p>
        <p className="mt-1 text-xs text-text-muted">
          {KIND_LABELS[asset.kind]} · {(asset.bytes / 1024).toFixed(0)} KB
          {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
        </p>
        {editing ? (
          <div className="mt-3 space-y-2">
            <Input
              placeholder="Opis (alt)"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
            />
            {asset.kind === 'team_photo' && team.length > 0 ? (
              <Select value={doc} onChange={(e) => setDoc(e.target.value)}>
                <option value="">— brak powiązania —</option>
                {team.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.full_name}
                  </option>
                ))}
              </Select>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Anuluj
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  onPatch({ alt_text: alt, linked_doctor_id: doc || null });
                  setEditing(false);
                }}
              >
                Zapisz
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex items-center justify-between gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Edytuj
            </Button>
            <div className="flex items-center gap-1">
              {needsConsent ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => onConsent({
                    subject_kind: asset.kind === 'team_photo' ? 'doctor' : 'patient',
                    subject_id: asset.linked_doctor_id ?? undefined,
                  })}
                >
                  Oznacz zgodę
                </Button>
              ) : null}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Usuń plik"
                onClick={onDelete}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}
